function isAbortError(error) {
  return Boolean(error && (error.name === 'AbortError' || error.code === 20));
}

function buildLoopbackFallbackUrl(url) {
  if (typeof url !== 'string' || !url) return null;
  if (url.includes('://127.0.0.1')) return url.replace('://127.0.0.1', '://localhost');
  if (url.includes('://localhost')) return url.replace('://localhost', '://127.0.0.1');
  return null;
}

function createApiFetch({ baseUrl, getToken, onCriticalError }) {
  const asText = (v) => String(v ?? '').toLowerCase();

  /** Assinaturas de infra do backend (SQL, PDO, MySQL, driver em falta). */
  const hasBackendInfraSignature = (body, message) => {
    const msg = asText(message);
    const blob = asText(
      [body?.message, body?.developerMessage, body?.exception, body?.error, JSON.stringify(body?.errors || {})].join(
        ' ',
      ),
    );
    const hay = `${msg} ${blob}`;
    if (hay.includes('sqlstate') || hay.includes('queryexception') || hay.includes('syntax error')) return true;
    if (hay.includes('pdoexception') || hay.includes('could not find driver')) return true;
    if (hay.includes('[2002]') || hay.includes('connection refused') || hay.includes('active refused')) return true;
    if (hay.includes('mysql server has gone away') || hay.includes('no such file or directory')) return true;
    const trace = asText(body?.trace?.[0] || '');
    return trace.includes('sql') || trace.includes('pdo');
  };

  const isDbConnectionLikely = (body, message) => {
    const hay = `${asText(message)} ${asText(body?.message)} ${asText(body?.exception)}`;
    return hay.includes('[2002]') || (hay.includes('connection refused') && hay.includes('mysql'));
  };

  function reportCritical(payload) {
    if (typeof onCriticalError === 'function') onCriticalError(payload);
  }

  return async function apiFetch(path, options = {}, tokenOverride) {
    const token = tokenOverride !== undefined ? tokenOverride : getToken?.();
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json', ...(options.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    const { signal, ...rest } = options;
    const url = `${baseUrl}${path}`;
    const fallbackUrl = buildLoopbackFallbackUrl(url);
    let response;
    try {
      response = await fetch(url, { ...rest, headers, ...(signal ? { signal } : {}) });
    } catch (e) {
      if (isAbortError(e)) throw e;
      if (fallbackUrl) {
        try {
          response = await fetch(fallbackUrl, { ...rest, headers, ...(signal ? { signal } : {}) });
        } catch {
          // Mantem o erro original para diagnostico consistente.
        }
      }
      if (!response) {
        const detail = e?.message || String(e);
        const msg =
          `Não foi possível contactar a API em ${baseUrl}. ` +
          'Confirme que o backend PHP está a correr (ex.: php -S 127.0.0.1:8000 -t backend-php/public), ' +
          'que o ficheiro frontend/.env define REACT_APP_API_URL se usar outro host/porta, ' +
          'e que reiniciou npm start após alterar o .env. ' +
          `(Detalhe técnico: ${detail})`;
        reportCritical({
          id: `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
          kind: 'network',
          title: 'Frontend sem ligação à API Sienge Pay',
          message: msg,
          path,
          detail: '',
        });
        const err = new Error(msg);
        err.network = true;
        err.cause = e;
        throw err;
      }
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const error = new Error(body.message || body.developerMessage || `Erro HTTP ${response.status}`);
      error.status = response.status;
      error.body = body;
      error.path = path;
      if (hasBackendInfraSignature(body, error.message)) {
        const dbConn = isDbConnectionLikely(body, error.message);
        reportCritical({
          id: `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
          kind: dbConn ? 'db' : 'sql',
          title: dbConn
            ? `Backend sem ligação ao MySQL (${response.status})`
            : `Erro SQL / base de dados no backend (${response.status})`,
          message: error.message,
          path,
          detail:
            (body?.developerMessage || body?.exception || '') +
            (dbConn
              ? ' — Na máquina local: `docker compose up -d` na raiz do projeto e `DB_*` em backend/.env alinhados a backend/.env.example.'
              : ''),
        });
      }
      throw error;
    }
    if (response.status === 204) return null;
    return response.json();
  };
}

export { createApiFetch, isAbortError };
